<template>
  <div class="bg-white">
    <div class="fluid-container-custom pb-32 relative" ref="container">
      <h1 class="text-2xl py-8">{{ donorName }}</h1>
      <div class="border-b border-Neutral-400 pb-8">
        <h3 class="mini-title">SPENDING CATEGORIES</h3>
        <aggregatedStackedHorizontal
          :series="allCategoriesReadyForStacked"
          :showLegend="false"
          :showLabels="false"
          :barHeight="60"
          :colorScaleExternal="categoryColorScale"
          rectClass="stroke-white stroke-2 hover:brightness-110"
          :roundedRadius="6"
          :axisWidth="0"
        ></aggregatedStackedHorizontal>
        <h3 class="mini-title mt-8">TOP 5 CATEGORIES</h3>
        <div class="flex gap-1 mb-8">
          <InterfaceLabelBadge
            v-for="(
              cat, index
            ) in allCategoriesReadyForStacked[0].children.slice(0, 5)"
            :key="index"
            :category="cat.id"
            parent-id="bydonor"
          />
        </div>
        <h3 class="mini-title">RECIPIENTS</h3>
        <div>
          <span
            v-for="(rec, index) in allRecipientsForTextList"
            :key="index"
            class="font-sans text-sm"
          >
            {{ rec.name }},
          </span>
        </div>
      </div>
      <filtersFilterPanel v-model:open="openFilters"></filtersFilterPanel>

      <section class="global-section-divider">
        <div class="mt-8 grid grid-cols-5">
          <div class="col-span-4">
            <div class="flex justify-start">
              <h2 class="h2">Who gives democracy support funding?</h2>
              <HelpTooltip>
                Click on the drop-down arrows to arrange the data by name or donation value. 
                <br/>
                This overview <span class="font-bold"> does include </span> bilateral funding to unspecified recipients and multilateral organisations.
          </HelpTooltip>
            </div>
            <p class="p-base mt-2">
              A visual overview of how each donor spends on core democracy
              support. Hover the mouse over the donor, recipient, or category
              labels to learn more about where the funding is channeled from
              each category of core democracy support.
            </p>
          </div>
          <div class="text-right">
            <button
              class="px-4 py-1 rounded-md font-medium inline-flex gap-x-1.5"
              :class="[
                ifStore.isAnyFilterActive === true
                  ? 'bg-Secondary-400 text-white'
                  : 'bg-white text-Primary-800 ring-1 ring-Primary-800',
              ]"
              @click="openFilters = true"
            >
              Filter
              <AdjustmentsVerticalIcon
                class="-mr-1 h-5 w-5 text-Primary-800"
                :class="[
                  ifStore.isAnyFilterActive === true
                    ? 'text-white'
                    : ' text-Primary-800 ',
                ]"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div class="mt-4 mb-4">
          <FiltersInlineFilters> </FiltersInlineFilters>
        </div>
        <SankeyHeaders :headers="headers" hideOptionsFor="Donor" />
        <div class="sankey-wrapper" style="height:800px">
        <SankeyChart
          v-if="isDataForSankeyReady"
          :showLegend="true"
          :chart-data="sankeyData"
          :height="800"
          :total-width="availableWidth"
          :auto-sorting="
            headers[0].sortBy === sortByTypes.Auto &&
            headers[1].sortBy === sortByTypes.Auto &&
            headers[2].sortBy === sortByTypes.Auto
          "
          @nodeSelected="onNodeSelected"
          :extraBilateralInfo="true"
        />
      </div>
        <interfaceEntitySneakPeakModal
          v-model:showModal="showSneakPeakModal"
          :entityName="selectedEntity.name"
          :entityId="selectedEntity.id"
          :categories="selectedEntity.categories"
          :recipients="selectedEntity.recipients"
          :contentType="selectedEntity.contentType"
          :nodeType="selectedEntity.nodeType"
          :filter-by-entity-name="donorName"
        >
        </interfaceEntitySneakPeakModal>
      </section>
      <section class="global-section-divider">
        <div class="mt-8 grid grid-cols-5 mt-5">
          <div class="col-span-4">
            <div class="flex justify-start">
              <h2 class="h2">How does democracy support funding evolve?</h2>
            </div>
            <p class="p-base mt-2 mb-16">
              Democracy support funding can fluctuate over time. The following
              graph shows how core democracy support has evolved between {{baseYearRange[0]}}
              and {{baseYearRange[1]}}. Hover over each category to see how much funding is
              connected to it.
            </p>
          </div>
        </div>

        <aggregatedStackedVertical
          :series="dataByYearGrouped"
          :showFigures="showFigures"
          :colorScaleExternal="categoryColorScale"
        ></aggregatedStackedVertical>
      </section>

      <section class="global-section-divider">
        <div class="mt-8 grid grid-cols-5 mt-5">
          <div class="col-span-4">
            <div class="flex justiy-start">
              <h2 class="h2">How is democracy support delivered?</h2>

            </div>
            <p class="p-base mt-2 mb-16">
              Democracy support can be delivered through different types of aid.
              The following diagram reveals how funding is delivered to
              recipents by the donor. Support is tailored to each context,
              therefore it varies greatly from donor to donor and recipient to
              recipient.
            </p>
          </div>
        </div>

        <!-- <chartsCircleAreaCategories
          :vizData="dataForBubbleChart"
          :formatTools="$formater.salaryFormater"
          :colorScale="
            linearColorScale.domain([
              0,
              d3.max(dataForBubbleChart, (d) => d.value),
            ])
          "
        ></chartsCircleAreaCategories> -->
        <ChartsPieChart
          :format-percentage="$formater.intFormater"
          :format-raw="$formater.salaryFormater"
          v-if="availableWidth > 0 && dataForBubbleChart.length > 0"
          :chartData="dataForBubbleChart"
          :base-size="{ w: availableWidth, h: 500 }"
          :label="donorName"
        >
          <template #inside-chart>
            {{ donorName }}
          </template>
          <template #above-legend>
            <div class="flex justify-between gap-4 items-center">
              <span class="text-Primary-400 text-xs">{{
                localYearFilter.minYear
              }}</span>
              <div class="flex-grow">
                <InterfaceSliderWrapper
                  @update:sliderYears="localYearFilter.sliderYears = $event"
                  :min="localYearFilter.minYear"
                  :max="localYearFilter.maxYear"
                ></InterfaceSliderWrapper>
              </div>
              <span class="text-Primary-400 text-xs">{{
                localYearFilter.maxYear
              }}</span>
            </div>
          </template>
        </ChartsPieChart>
      </section>
    </div>
  </div>
  <div>
  <InterfaceSourcesText ></InterfaceSourcesText>
  </div>
</template>

<script setup>
import {
  categoryColorScale,
  _Categories2Array,
  baseYearRange,
} from "@/assets/baseTypes.js";
import { AdjustmentsVerticalIcon } from "@heroicons/vue/20/solid";
import { sortByTypes, nodeTypes } from "@/assets/configStrings.js";

import { useRoute } from "vue-router";
import { queries } from "@/assets/queries.js";
import { httpConfig } from "@/assets/baseTypes.js";
import recipients from "@/assets/recipients.json";

import * as d3 from "d3";
import aidCodes from "@/assets/aid_codes.json";
detectScreenSize();
const container = ref(null);
const { width: availableWidth, height: availableHeight } =
  useElementSize(container);
const config = useRuntimeConfig();

const donorId = ref(null);
const route = useRoute();

donorId.value = route.params.id.toUpperCase();
// Make API call to fetch donor data using the donorId.value variable

const store = useBaseDataStore();

const ifStore = useInterfaceStore();

/** make sure token is available in ssr or hydration */

// await useManageAuth()
/*** */

// const reloadOnError = useReloadTokenOnError();

const donorName = computed(() => {
  return store.allDonorNames.find((item) => item.iso === donorId.value)?._id;
});

const headers = ref([
  {
    text: "Donor",
    sortBy: sortByTypes.Auto,
  },

  {
    text: "Categories",
    sortBy: sortByTypes.Auto,
  },
  {
    text: "Recipients",
    sortBy: sortByTypes.Auto,
  },
]);

// This is actually the data from the middle to the second part
const {
  data: resultsForSankey1,
  pending: pending1,
  refresh: refresh1,
} = await useAsyncData(
  'sankey1' + donorId.value +'-'+ ifStore.sliderYears[0]+''+ifStore.sliderYears[1],
  () => {
    const configAllRecipients = httpConfig(
      JSON.stringify(
        queries.getAllRecipientsByCategoryFilterByDonor(
          donorId.value,
          ifStore.sliderYears
        )
      )
    );
    return $fetch(config.public.apiURL, configAllRecipients).catch((error) => {
      console.log("error dentro de fetch");
      reloadOnError.refreshReload();
    });
  },
  {
    transform: (data) => {
      if(data?.documents === undefined) return []
      data.documents.forEach((item) => {
        item.label = recipients.find(
          (rec) => rec.recipient_code == item._id[0]
        )?.name;
        item.label = item.label ? item.label : "Bilateral, unspecified";
      });
      return data.documents;
    },
  }
);

const allRecipientsForTextList = computed(() => {
  // remove recipient with .name 0 'others' or 'Others' and sort by value
  return allRecipients.value
    .filter((item) => item.name !== "Others" && item.name !== "others")
    .sort((a, b) => b.TOTAL - a.TOTAL);
});

const resultsForSankey1Filtered = computed(() => {
  // this function will filter out the categories not selected in the filters
  let documents = resultsForSankey1.value;
  if (ifStore.isFilterActive === true) {
    const filteredCategories = resultsForSankey1.value.filter((item) => {
      return ifStore.activeFilterKeys.includes(item._id[1]);
    });
    documents = filteredCategories;
  }
  // get Total using d3.rollup
  const total = d3.sum(documents, (d) => d.TOTAL);
  // add percentage to the array
  documents.forEach((item) => {
    item.percentage = (item.TOTAL / total) * 100;
  });
  // END: low-contributions
  return documents;
});

const {
  data: resultsForSankey2,
  pending: pending2,
  refresh: refresh2,
} = await useAsyncData(
  'sankey2'+donorId.value +'-'+ ifStore.sliderYears[0]+''+ifStore.sliderYears[1],
  () => {
    const configDonors = httpConfig(
      JSON.stringify(
        queries.getAllCategoriesOneDonor(donorId.value, ifStore.sliderYears)
      )
    );
    return $fetch(config.public.apiURL, configDonors).catch((error) => {
      console.log("error dentro de fetch");
      reloadOnError.refreshReload();
    });
  },
  {
    transform: (data) => {
      if(data?.documents === undefined) return []
      return data.documents;
    },
  }
);

const resultsForSankey2Filtered = computed(() => {
  // this function will filter out the categories not selected in the filters
  if (ifStore.isFilterActive === false) return resultsForSankey2.value;
  else {
    const filteredCategories = resultsForSankey2.value.filter((item) => {
      return ifStore.activeFilterKeys.includes(item._id);
    });
    return filteredCategories;
  }
});

const {
  sankeyData,
  allCategoriesThisDonor,
  allRecipients,
  nodesWithLowPercentage,
} = useGetDataForSankey(
  resultsForSankey1Filtered,
  resultsForSankey2Filtered,
  pending1,
  pending2,
  donorId,
  donorName,
  headers
);

const isDataForSankeyReady = computed(() => {
  return sankeyData.value.nodes.length > 0 && sankeyData.value.links.length > 0;
});

watch(
  () => ifStore.sliderYears,
  (newValue, oldValue) => {
    refresh2();
    refresh1();
  }
);

//** prepare data for stacked */
const { data: dataByYear, refresh: refreshStacked } = await useAsyncData(
  "byYEar" + donorId.value,
  () => {
    const configByYear = httpConfig(
      JSON.stringify(queries.getTotalByYearCategoryOneDonor(donorId.value))
    );
    return $fetch(config.public.apiURL, configByYear).catch((error) => {
      console.log("error dentro de fetch");
      reloadOnError.refreshReload();
    });
  }
);

const dataByYearGrouped = computed(() => {
  // returns an array with the structure
  // [{year: 2015, values: [{category: categoryname1, value:TOTAL },{category: categoryname2, value:TOTAL }
  // {year: 2016, values: [{category: categoryname1, value:TOTAL },{category: categoryname2, value:TOTAL }
  // ]
  const dataByYearGrouped = d3.rollups(
    dataByYear.value.documents,
    (v) => d3.sum(v, (d) => d.TOTAL),
    (d) => d._id[0],
    (d) => d._id[1]
  );
  const dataByYearGroupedArray = Array.from(dataByYearGrouped).map((item) => {
    const groupSum = d3.sum(item[1], (d) => d[1]);
    return {
      id: item[0], //year
      value: groupSum,
      children: item[1]
        .map((item2) => {
          return {
            id: item2[0],
            value: item2[1],
            valueRelative: item2[1] / groupSum,
          };
        })
        .sort((a, b) => d3.ascending(b.id, a.id)),
    };
  });
  return dataByYearGroupedArray.sort((a, b) => parseInt(a.id) - parseInt(b.id));
});

//*** click on sankey interaction */
// part in control of the sneakPeak Modal
const recipientsByRegion = d3.group(recipients, (d) => d.region);

const showSneakPeakModal = ref(false);
const selectedEntity = ref({
  name: "",
  id: "",
  type: "single",
  topCountries: [],
  topCategories: [],
});
const onNodeSelected = (node) => {
  if (node.id === "Others") {
    showSneakPeakModal.value = true;
    selectedEntity.value.contentType = "multiple";
    selectedEntity.value.nodeType = node.type;
    selectedEntity.value.name = node.name;
    selectedEntity.value.recipients = nodesWithLowPercentage.value.sort(
      (a, b) => d3.ascending(b.TOTAL, a.TOTAL)
    );
  } else if (node.id == "NA") {
    showSneakPeakModal.value = false;
  } else if (node.type === nodeTypes.recipient) {
    showSneakPeakModal.value = true;
    selectedEntity.value.contentType = "single";
    selectedEntity.value.nodeType = node.type;
    selectedEntity.value.name = node.name;
    selectedEntity.value.id = node.id;
    selectedEntity.value.categories = node.targetLinks.map((link) => {
      return {
        id: link.source.name,
        value: link.value,
      };
    });
  }
};

/*** stacked */

const allCategoriesReadyForStacked = computed(() => {
  const allCategoriesMap = d3.rollup(
      resultsForSankey2.value,
      (v) => d3.sum(v, (d) => d.TOTAL),
      (d) => d._id
    );
    // iterate over the map and return an array of objects
    const allCategoriesThisDonorNotFiltered = Array.from(allCategoriesMap, ([name, value]) => ({
      id: name,
      value: value,
    }))
  
  const categoryTotal = d3.sum(allCategoriesThisDonorNotFiltered, (d) => d.value);
  return [
    {
      id: "",
      value: categoryTotal,
      children: allCategoriesThisDonorNotFiltered
        .map((item) => {
          return {
            ...item,
            valueRelative: item.value / categoryTotal,
          };
        })
        .sort((a, b) => b.value - a.value),
    },
  ];
});

const showFigures = ref(false);
const openFilters = ref(false);

const localYearFilter = reactive({
  minYear: baseYearRange[0],
  maxYear: baseYearRange[1],
  sliderYears: baseYearRange,
});

// Get data for the bubble chart:
const { data: dataForBubbleChart, refresh: refreshBubbleData } =
  await useAsyncData(
    "bubble" + donorId.value + "" + localYearFilter.sliderYears,
    () => {
      const configBubble = httpConfig(
        JSON.stringify(
          queries.getAllCategoriesByDonor(
            donorId.value,
            localYearFilter.sliderYears
          )
        )
      );
      return $fetch(config.public.apiURL, configBubble).catch((error) => {
        console.log("error dentro de fetch");
        reloadOnError.refreshReload();
      });
    },
    {
      transform: (data) => {
        const total = d3.sum(data.documents, (d) => d.TOTAL);
        const allItems = data.documents
          .map((d) => {
            return {
              id: d._id,
              value: d.TOTAL,
              value_relative: d.TOTAL / total,
              label: aidCodes.find((item) => item.aid_t == d._id)?.name,
              unit: "USD",
            };
          })
          .sort((a, b) => b.value - a.value);
        // return only the first 5 elements and group the others into one category:
        const firstFive = allItems.slice(0, 4);
        const others = {
          id: "Others",
          value: d3.sum(allItems.slice(4), (d) => d.value),
          value_relative: d3.sum(allItems.slice(4), (d) => d.value_relative),
          label: "Others",
          unit: "USD",
          color: "#3333",
        };
        return [...firstFive, others];
      },
    }
  );

watch(localYearFilter, (newValue, oldValue) => {
  refreshBubbleData();
});
</script>
